import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, ReportTemplate, ReportTemplateTypes } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleTablePremium from "../../components/tables/CollapsibleTablePremium";
import TitleBox from "../../components/TitleBox";
import { createCustomToolbarProps } from "../../components/utility/CustomToolbarProps";
import { StyledLink } from "../../components/utility/StyledLink";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";
import usePermissions from "../../hooks/auth/usePermissions";
import useReportTemplates from "../../hooks/data/useReportTemplates";

const useEmailOnlyTemplateColumns = () => {
    const templateColumns : GridColDef[] = useMemo(() => {
        const columns = [
            {
                field: 'name',
                headerName: 'Name',
                minWidth: 200,
                flex: 1
            }
        ]

        return columns;
    }, [])

    return templateColumns
}

const usePredefinedTemplateColumns = () => {
    const templateColumns : GridColDef[] = useMemo(() => {
        const columns = [
            {
                field: 'name',
                headerName: 'Name',
                minWidth: 200,
                flex: 1,
                renderCell: (params: GridRenderCellParams) => (
                    <StyledLink
                        to={`/reports/templates/preview/${params.row.id}`}
                    >
                        {params.formattedValue}
                    </StyledLink>
                ),
            }
        ]

        return columns;
    }, [])

    return templateColumns
}

const useCustomTemplateColumns = () => {
    const templateColumns = useMemo(() => {
        const columns : GridColDef<ReportTemplate>[] = [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 200,
                renderCell: (params: GridRenderCellParams) => (
                    <StyledLink
                        to={`/reports/templates/edit/${params.row.id}`}
                    >
                        {params.formattedValue}
                    </StyledLink>
                )
            },
            {
                field: 'actions',
                headerName: '',
                renderCell: (params) => (
                    <GridActions 
                        editUrl={`/reports/templates/edit/${params.row.id}`}
                        deleteUrl={`/reports/templates/delete/${params.row.id}`}
                    />
                ),
                disableExport: true
            }
        ]

        return columns;
    }, [])

    return templateColumns
}

const ReportTemplates: FC = () => {
    const {data: permissions} = usePermissions();

    const emailOnlyTemplateColumns = useEmailOnlyTemplateColumns();
    const predefinedTemplateColumns = usePredefinedTemplateColumns();
    const customTemplateColumns = useCustomTemplateColumns();

    const contextValue = useReportTemplates();
    const {data: templates} = contextValue;

    const emailOnlyTemplates = useMemo(() => {
        return templates?.filter(template => 
            template.templateType == ReportTemplateTypes.EmailOnlyLotSummary ||
            template.templateType == ReportTemplateTypes.EmailOnlySalesSummary
        ) ?? [];
    }, [templates]);

    const predefinedTemplates = useMemo(() => {
        return templates?.filter(template => 
            template.templateType != ReportTemplateTypes.Custom && 
            template.templateType != ReportTemplateTypes.EmailOnlyLotSummary &&
            template.templateType != ReportTemplateTypes.EmailOnlySalesSummary
        ) ?? [];
    }, [templates]);

    const customTemplates = useMemo(() => {
        return templates?.filter(template => template.templateType == ReportTemplateTypes.Custom) ?? [];
    }, [templates]);

    const predefinedTemplatesCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Predefined Report Templates`,
        exportTitle: `Predefined Report Templates`,
        includeDateTime: true
    }), []);
    
    const customTemplatesCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        fileName: `Custom Report Templates`,
        exportTitle: `Custom Report Templates`,
        includeDateTime: true
    }), []);

    return (
        <ReportTemplatesContext.Provider value={contextValue}>
            <SetLinks
                stageLinkEnabled={false}
                lotLinkEnabled={false}
            />
            <Outlet />
            <TitleBox
                sx={{
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                {
                    permissions?.includes(Permissions.ReportsCanViewCustomReports) &&
                    <StyledButton
                        LinkComponent={Link}
                        //@ts-expect-error
                        to={"/reports/templates/create"}
                    >
                        Create New Template
                    </StyledButton>
                }
            </TitleBox>
            <CollapsibleTablePremium 
                density="compact"
                title={"Email Only Templates"} 
                loading={false} 
                columns={emailOnlyTemplateColumns}    
                rows={emailOnlyTemplates}
                disableColumnMenu={false}
                disableColumnFilter={false}
                {...predefinedTemplatesCustomToolbarProps}
            />
            <CollapsibleTablePremium 
                density="compact"
                title={"Predefined Templates"} 
                loading={false} 
                columns={predefinedTemplateColumns}    
                rows={predefinedTemplates}
                disableColumnMenu={false}
                disableColumnFilter={false}
                {...predefinedTemplatesCustomToolbarProps}
            />
            {
                permissions?.includes(Permissions.ReportsCanViewCustomReports) &&
                <CollapsibleTablePremium 
                    density="compact"
                    title={"Custom Templates"} 
                    loading={false}
                    columns={customTemplateColumns}
                    rows={customTemplates}
                    disableColumnMenu={false}
                    disableColumnFilter={false}
                    {...customTemplatesCustomToolbarProps}
                />
            }
        </ReportTemplatesContext.Provider>
    );
}

export default ReportTemplates;